import React from "react";

import cx from "classnames";

import { Fieldset } from "../Fieldset";
import { Flex } from "../Flex";
import { Spacer } from "../Grid";
import { Icon } from "../Icon";
import { Text } from "../Text";

import styles from "./Checkbox.scss";

export interface CheckboxGroupProps {
  /**
   * Pass in one or more Checkbox components
   */
  children: React.ReactNode;
  /**
   * Whether the layout of the checkboxes should be horizontal or not
   */
  horizontal?: boolean | null;
  /**
   * Whether all checkboxes in the group should be disabled
   */
  disabled?: boolean | null;
  /**
   *  Wraps the CheckboxGroup in a &lt;Fieldset&gt; for accessibility
   */
  label?: React.ReactNode;
  /**
   * Helper text to render under the label
   */
  helperText?: React.ReactNode;
  /**
   * Error message to render under the label
   */
  error?: string | JSX.Element | null;
  /**
   * By default, the fieldset is not highlighted. Pass a variant here to make it stand out.
   * (Only applies if you pass a `label` prop.)
   */
  fieldsetVariant?: "highlighted" | "transparent";
  /**
   * Data attribute for testing purposes
   */
  "data-testid"?: string;
}

/**
 * A checkbox control wrapper
 */
export function CheckboxGroup({
  children,
  error,
  horizontal,
  label,
  disabled,
  helperText,
  "data-testid": dataTestId = "confetti-checkbox-group",
  fieldsetVariant,
}: React.PropsWithChildren<CheckboxGroupProps>): JSX.Element {
  const checkboxCount = React.Children.count(children) - 1;
  const className = cx({
    [styles["horizontal"]]: horizontal,
    [styles["group-of-checkbox"]]: label,
  });

  const clonedChildren = React.Children.map(children, (child, i) => {
    // istanbul ignore next - type guard. Doesn't happen in practice, but we'll check and bail early here just in case
    if (!child || typeof child !== "object") {
      return null;
    }
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const childAsReactElement = child as React.ReactElement;
    // child has its own margin style
    const childHasHelperText = Boolean(childAsReactElement.props.helperText);
    const isLast = i === checkboxCount;

    const classes = cx(
      childAsReactElement.props.className,
      {
        [styles.checkboxSpace]: !isLast && !childHasHelperText,
      },
      styles["checkbox-isgrouped"]
    );

    const testId = childAsReactElement.props["data-testid"]
      ? childAsReactElement.props["data-testid"]
      : `${dataTestId}-${i}`;

    return React.cloneElement(childAsReactElement, {
      disabled,
      className: classes,
      "data-testid": testId,
      hasError: Boolean(error),
    });
  });

  if (label) {
    return (
      <Fieldset
        data-testid={dataTestId}
        label={label}
        disabled={Boolean(disabled)}
        legendType="basic"
        variant={fieldsetVariant}
      >
        <Flex>
          {error && (
            <>
              <Icon icon="warning" size={12} color={styles.systemRed} />
              <Spacer size="tiny" orientation="horizontal" />
            </>
          )}
          <HelperText
            content={error}
            classNameString="group-helper-text--error"
          />
        </Flex>
        <HelperText content={helperText} classNameString="group-helper-text" />
        <div className={className}>{clonedChildren}</div>
      </Fieldset>
    );
  }

  return (
    <div data-testid={dataTestId}>
      <Flex alignItems="center">
        {error && (
          <div>
            <Icon icon="warning" size={12} color={styles.systemRed} />
            <Spacer size="tiny" orientation="horizontal" />
            <Spacer size="small" />
          </div>
        )}
        <HelperText
          content={error}
          classNameString="group-helper-text--error"
        />
      </Flex>
      <HelperText content={helperText} classNameString="group-helper-text" />
      <div className={className}>{clonedChildren}</div>
    </div>
  );
}

interface HelperTextProps {
  content?: React.ReactNode;
  classNameString: string;
}

function HelperText(props: HelperTextProps) {
  if (!props.content) {
    return null;
  }

  return (
    <Text
      size="paragraphXsmall"
      paragraph
      className={styles[props.classNameString]}
    >
      {props.content}
    </Text>
  );
}
